import { useQueries, useQuery } from '@tanstack/react-query';
import { Card, Descriptions, Dropdown, Modal, Table, Tag } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { ReactComponent as Plus } from '../../../assets/add.svg';
import { ReactComponent as Ellipsis } from '../../../assets/ellipsis.svg';
import { getPrivileges, getPermissions, getRoles } from '../request';
import Button from '../../../custom/button/button';
import { useState, useMemo } from 'react';
import AddPrivilege from './addPrivilege';
import EditPrivilege from './editPrivilege';
import { getErrorMessage } from '../../../utils/getErrorMessage';

const Privileges = () => {
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openView, setOpenView] = useState(false);
  const [selectedPrivilege, setSelectedPrivilege] = useState<Privilege | null>(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['get-all-privileges'],
    queryFn: getPrivileges,
  });

  const [rolesQuery, permissionsQuery] = useQueries({
    queries: [
      { queryKey: ['get-roles'], queryFn: getRoles },
      { queryKey: ['get-all-permissions'], queryFn: getPermissions },
    ],
  });

  const toggleAddModal = () => setOpenAdd((prev) => !prev);
  const toggleEditModal = () => setOpenEdit((prev) => !prev);
  const toggleViewModal = () => setOpenView((prev) => !prev);

  const handleEdit = (record: Privilege) => {
    setSelectedPrivilege(record);
    toggleEditModal();
  };

  const handleView = (record: Privilege) => {
    setSelectedPrivilege(record);
    toggleViewModal();
  };

  const roles = rolesQuery?.data as Role[];
  const permissions = permissionsQuery?.data?.data as Permission[];

  const roleMap = useMemo(() => {
    return roles?.reduce(
      (acc, role) => { acc[role.id] = role.name; return acc; },
      {} as Record<string, string>,
    );
  }, [roles]);

  const permissionMap = useMemo(() => {
    return permissions?.reduce(
      (acc, permission) => { acc[permission.id] = permission.name; return acc; },
      {} as Record<number, string>,
    );
  }, [permissions]);

  const columns: ColumnsType<Privilege> = [
    {
      key: 'S/N',
      title: 'S/N',
      render: (_text, _record, index) => index + 1,
    },
    {
      key: 'roleId',
      title: 'Role',
      dataIndex: 'roleId',
      render: (roleId: string) => roleMap?.[roleId] || roleId,
    },
    {
      key: 'permissionId',
      title: 'Permissions',
      dataIndex: 'permissionId',
      render: (permissionIds: number[]) => (
        <span>
          {permissionIds?.map((id) => (
            <Tag key={id}>{permissionMap?.[id] || id}</Tag>
          ))}
        </span>
      ),
    },
    {
      key: 'institutionShortName',
      title: 'Institution',
      dataIndex: 'institutionShortName',
    },
    {
      key: 'createdDate',
      title: 'Created Date',
      dataIndex: 'createdDate',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      key: 'action',
      title: 'Action',
      render: (_, record) => (
        <Dropdown
          menu={{
            items: [
              {
                label: 'View Privilege',
                key: 'view',
                onClick: () => handleView(record),
              },
              {
                label: 'Edit Privilege',
                key: 'edit',
                onClick: () => handleEdit(record),
              },
            ],
          }}
          trigger={['click']}
        >
          <Ellipsis />
        </Dropdown>
      ),
    },
  ];

  const privilegeData = data?.data as Privilege[];

  return (
    <div>
      <section className="space-between">
        <h3>Privileges</h3>
        <Button text="Add Privilege" iconBefore={<Plus />} onClick={toggleAddModal} />
      </section>

      <br />

      <Card bordered={false}>
        <Table
          dataSource={privilegeData}
          columns={columns}
          rowKey={(record) => record.id}
          scroll={{ x: true }}
          loading={isLoading || rolesQuery.isLoading || permissionsQuery.isLoading}
          pagination={false}
          locale={{ emptyText: isError ? getErrorMessage(error) : 'No privileges found' }}
        />
      </Card>

      <Modal title="Add Privilege" open={openAdd} onCancel={toggleAddModal} footer={null}>
        <AddPrivilege handleClose={toggleAddModal} refetch={refetch} />
      </Modal>

      <Modal title="Edit Privilege" open={openEdit} onCancel={toggleEditModal} footer={null}>
        {selectedPrivilege && (
          <EditPrivilege privilege={selectedPrivilege} handleClose={toggleEditModal} refetch={refetch} />
        )}
      </Modal>

      <Modal title="View Privilege" open={openView} onCancel={toggleViewModal} footer={null}>
        {selectedPrivilege && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Role">
              {roleMap?.[selectedPrivilege.roleId] || selectedPrivilege.roleId}
            </Descriptions.Item>
            <Descriptions.Item label="Permissions">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {selectedPrivilege.permissionId?.length ? (
                  selectedPrivilege.permissionId.map((id) => (
                    <Tag key={id}>{permissionMap?.[id] || id}</Tag>
                  ))
                ) : (
                  <span style={{ color: '#999' }}>No permissions assigned</span>
                )}
              </div>
            </Descriptions.Item>
            <Descriptions.Item label="Institution">
              {selectedPrivilege.institutionShortName}
            </Descriptions.Item>
            <Descriptions.Item label="Created Date">
              {new Date(selectedPrivilege.createdDate).toLocaleDateString()}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default Privileges;
